import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Field,
  Label,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Input,
} from '@fluentui/react-components';
import React from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { OTHER } from '../../../app/common/constants/SiteConstants';
import { DoubleRequiredMarker } from '../../common/formFields/RequiredMarker';
import AuditingFirmsDropdown from '../../companies/detail/forms/AuditingFirmsDropdown';
import OversightGroupDropdown from '../../companies/fiscalYears/OversightGroupDropdown';
import RequiredFieldsNotification from '../RequiredNotification';
import { IStatutoryInformation, RequiredFieldsType } from './types';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';

interface IStatutoryInformationFormProps {
  form: UseFormMethods<IStatutoryInformation>;
  data: IStatutoryInformation;
}

const requiredLabelStyles = { paddingBottom: 0 };

const StatutoryInformationForm = ({ data, form }: IStatutoryInformationFormProps): JSX.Element => {
  const { control, errors, setValue, watch } = form;

  const auditingFirm = watch('auditingFirm');

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <RequiredFieldsNotification type={RequiredFieldsType.StatSetup} />
        <Controller
          name="oversightGroup"
          control={control}
          defaultValue={data?.oversightGroup}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <>
              <Label style={requiredLabelStyles}>
                Oversight Group <DoubleRequiredMarker />
              </Label>
              <OversightGroupDropdown
                valueKey="oversightGroup"
                ariaLabel="Oversight Group Dropdown"
                errors={errors}
                handleChange={(newValue: string, valueKey: string): void => {
                  setValue('oversightGroup', newValue);
                }}
                value={value}
              />
            </>
          )}
        />
        <Controller
          name="isMcaps"
          defaultValue={data?.isMcaps}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="MCAPS Flag?" required>
                <RadioGroup
                  id="isMcaps"
                  name="isMcaps"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('isMcaps', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="isOneFinance"
          defaultValue={data?.isOneFinance}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="One Finance Flag?" required>
                <RadioGroup
                  id="isOneFinance"
                  name="isOneFinance"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('isOneFinance', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="isFsAuditLegallyRequired"
          defaultValue={data?.isFsAuditLegallyRequired}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="F/S Audit Legally Required by Regulator?" required>
                <RadioGroup
                  id="isFsAuditLegallyRequired"
                  name="isFsAuditLegallyRequired"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('isFsAuditLegallyRequired', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="auditingFirm"
          defaultValue={data?.auditingFirm?.toString().toLowerCase()}
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <>
              <Label style={requiredLabelStyles}>
                Auditing Firm <DoubleRequiredMarker />
              </Label>
              <AuditingFirmsDropdown
                valueKey="auditingFirm"
                ariaLabel="Auditing Firm Dropdown"
                errors={errors}
                value={value}
                handleChange={(newValue: string, valueKey: string): void => {
                  setValue('auditingFirm', newValue);
                  if (newValue !== OTHER) {
                    setValue('otherAuditingFirm', null);
                  }
                }}
              />
            </>
          )}
        />
        <Controller
          control={control}
          name="otherAuditingFirm"
          defaultValue={data?.otherAuditingFirm || ''}
          rules={{
            maxLength: {
              value: 75,
              message: 'Other Auditing Firm can only be a max of 75 characters',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={requiredLabelStyles}>
                  Other Auditing Firm
                  {auditingFirm?.fieldName === OTHER ? <DoubleRequiredMarker /> : null}
                </Label>
              }
              validationMessage={errors?.otherAuditingFirm?.message}
            >
              <Input
                id="otherAuditingFirm"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
                disabled={auditingFirm ? auditingFirm.fieldName !== OTHER : true}
                aria-label="Other Auditing Firm input"
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="einTaxNumber"
          defaultValue={data?.einTaxNumber || ''}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={requiredLabelStyles}>
                  EIN Tax Number
                  <DoubleRequiredMarker />
                </Label>
              }
              validationMessage={errors?.einTaxNumber?.message}
            >
              <Input
                id="einTaxNumber"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
                aria-label="EIN Tax Number input"
              />
            </Field>
          )}
        />
        <Controller
          name="controllerMustBeFte"
          defaultValue={data?.controllerMustBeFte}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Controller Must Be FTE?" required>
                <RadioGroup
                  id="controllerMustBeFte"
                  name="controllerMustBeFte"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('controllerMustBeFte', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default StatutoryInformationForm;
